<template>
  <div class="animated fadeIn">
    <b-row class="py-2">
      <b-card :header="isDuplicate ? 'Duplicar Campaña' : 'Crear Campaña'" class="w-100">
        <b-form @submit.prevent="store" enctype="multipart/form-data">
          <b-row class="px-3">

            <b-form-group label="<strong>Nombre *</strong>" label-for="name" :horizontal="false" class="w-100">
              <b-form-input id="name" type="text" placeholder="Nombre" v-model="campaign.name" required></b-form-input>
            </b-form-group>

            <b-form-group label="<strong>Fecha de envío</strong>" label-for="publishedAt" class="w-100">
              <vue2-date-picker
                v-model="campaign.publishedAt"
                type="datetime"
                :format="`DD/MM/YYYY HH:MM`"
                :time-picker-options="timePickerOptions"
                :lang="lang"
                :editable="false"
                :first-day-of-week="1"
                :disabled="!!limitForm"
                class="w-100 date-picker-w-100 date-picker-custom-border">
              </vue2-date-picker>
            </b-form-group>

            <b-form-group label="<strong>Nombre de envío</strong>" label-for="senderName" :horizontal="false" class="w-100">
              <b-form-input id="senderName" type="text" placeholder="Nombre de envío" v-model="campaign.senderName"></b-form-input>
            </b-form-group>

            <b-form-group label="<strong>Email de envío</strong>" label-for="sender" :horizontal="false" class="w-100"
              :state="getEmailValidationState()" :invalid-feedback="getEmailErrorMessage()">
              <b-form-input id="sender" type="email" placeholder="Email de envío" v-model="campaign.sender"></b-form-input>
            </b-form-group>

            <b-form-group label="<strong>Email de respuesta</strong>" label-for="responder" :horizontal="false" class="w-100"
              :state="getResponseEmailValidationState()" :invalid-feedback="getResponseEmailErrorMessage()">
              <b-form-input id="responder" type="email" placeholder="Email de respuesta" v-model="campaign.responder"></b-form-input>
            </b-form-group>

            <b-form-group label="<strong>Emails adicionales</strong>" label-for="emails" :horizontal="false" class="w-100"
              :state="getEmailsValidationState()" :invalid-feedback="getEmailsErrorMessage()">
              <b-form-textarea
                id="emails"
                v-model="emailsText"
                placeholder="Introduce emails adicionales separados por coma o línea nueva"
                rows="3"
                @input="updateEmails"
              ></b-form-textarea>
              <small class="text-muted">Puedes añadir múltiples emails separados por comas o líneas nuevas</small>
            </b-form-group>

            <b-form-group label="<strong>Tipo*</strong>" label-for="campaignTypes" :horizontal="false" class="w-100">
              <multiselect
                name="campaignTypes"
                v-model="campaign.campaignTypes"
                :options="campaignTypes"
                track-by="uuid"
                label="name"
                :multiple="true"
                :allowEmpty="false"
                placeholder="Tipo"
                selectLabel="+"
                deselectLabel="-">
              </multiselect>
              <small v-if="submitAttempted && (!campaign.campaignTypes || campaign.campaignTypes.length === 0)" class="text-danger">
                Este campo es obligatorio
              </small>
            </b-form-group>

            <b-form-group label="<strong>Lista de Distribución* </strong>" label-for="distribution" :horizontal="false" class="w-100">
              <multiselect
                name="distribution"
                v-model="campaign.distributionLists"
                :options="distributionLists"
                track-by="uuid"
                label="name"
                :multiple="true"
                :allowEmpty="false"
                placeholder="Distribución"
                selectLabel="+"
                deselectLabel="-">
              </multiselect>
              <small v-if="submitAttempted && (!campaign.distributionLists || campaign.distributionLists.length === 0)" class="text-danger">
                Este campo es obligatorio
              </small>
            </b-form-group>

            <!-- Editor selector -->
            <b-form-group label="<strong>Editor de plantilla</strong>" class="w-100">
              <div class="editor-toggle">
                <button
                  type="button"
                  :class="['editor-toggle-btn', selectedEditor === 'ckeditor' ? 'active' : '']"
                  @click="switchEditor('ckeditor')"
                >
                  <i class="fa fa-code mr-1"></i> Editor Clásico
                </button>
                <button
                  type="button"
                  :class="['editor-toggle-btn', selectedEditor === 'grapes' ? 'active' : '']"
                  @click="switchEditor('grapes')"
                >
                  <i class="fa fa-th-large mr-1"></i> Editor por Bloques
                </button>
              </div>
            </b-form-group>

            <!-- Plantilla (Cambio crucial de v-show a v-if aquí abajo) -->
            <b-form-group class="w-100">
              <template #label>
                <strong>Plantilla de campaña *</strong>
              </template>

              <!-- GrapesJS -->
              <div v-if="selectedEditor === 'grapes'">
                <div id="gjs" style="height: 800px; border: 1px solid #e5e7eb; background: white;"></div>
              </div>

              <!-- CKEditor -->
              <div v-if="selectedEditor === 'ckeditor'">
                <ckeditor
                  :editor="editor"
                  id="editor"
                  name="html"
                  v-model="campaign.html"
                  :config="editorConfig"
                ></ckeditor>
              </div>
            </b-form-group>

          </b-row>

          <b-row>
            <b-col>
              <b-form-group label="<strong>Archivos adjuntos</strong>" label-for="attachment">
                <b-form-file :multiple="true" id="attachment" v-model="campaign.attachments" placeholder="Seleccione Archivos adjuntos">
                </b-form-file>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <div class="col float-left">
              <button type="submit" class="btn btn-brand btn-primary btn-lg pull-right"
                v-if="mixinCheckPermission('CAMPAIGN_NEW')">
                Guardar
              </button>
            </div>
          </b-row>

        </b-form>
      </b-card>
    </b-row>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Vue2DatePicker from 'vue2-datepicker';
import { getDistributions } from "@/api/distribution-list-api";
import { getCampaignTypes } from "@/api/campaign-type-api";

// GrapesJS
import grapesjs from "grapesjs/dist/grapes.min.js";
import "grapesjs/dist/css/grapes.min.css";
import newsletter from "grapesjs-preset-newsletter";
import { normalizeCampaignHtml } from '@/utils/campaignHtmlAdapter';

// CKEditor
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Code, Italic, Strikethrough, Subscript, Superscript, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { Font } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import { AutoImage, Image, ImageCaption, ImageInsert, ImageResize, ImageStyle, ImageToolbar } from '@ckeditor/ckeditor5-image';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { AutoLink, Link, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { PageBreak } from '@ckeditor/ckeditor5-page-break';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { ShowBlocks } from '@ckeditor/ckeditor5-show-blocks';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { SpecialCharacters, SpecialCharactersEssentials } from '@ckeditor/ckeditor5-special-characters';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
import { ImportWord } from '@ckeditor/ckeditor5-import-word';
import { ExportPdf } from '@ckeditor/ckeditor5-export-pdf';
import { ExportWord } from '@ckeditor/ckeditor5-export-word';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar } from '@ckeditor/ckeditor5-table';

export default {
  name: 'addCampaigns',
  components: { Multiselect, Vue2DatePicker },

  data() {
    return {
      submitAttempted: false,
      selectedEditor: 'ckeditor',
      gjs: null,

      // CKEditor config
      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          Autoformat, BlockQuote, Bold, Heading, Image, ImageCaption,
          ImageStyle, ImageToolbar, Indent, Italic, Link, List, MediaEmbed,
          Paragraph, Table, TableToolbar, Alignment, AutoImage, AutoLink,
          Code, CodeBlock, Essentials,
          FindAndReplace, Font, Highlight, HorizontalLine,
          HtmlEmbed, ImageInsert, ImageResize, IndentBlock,
          LinkImage, ListProperties, TodoList, Mention, PageBreak, PasteFromOffice,
          RemoveFormat, ShowBlocks, SourceEditing,
          SpecialCharacters, SpecialCharactersEssentials,
          Underline, WordCount,
          CloudServices, ExportPdf, ExportWord, ImportWord,
          TableCaption, TableCellProperties, TableColumnResize,
          TableProperties,
          Strikethrough, Subscript, Superscript,
        ],
        toolbar: {
          items: [
            'undo', 'redo', '|',
            'exportPdf', 'exportWord', 'importWord', '|',
            'showBlocks', 'findAndReplace', 'selectAll', '|',
            'heading', '|',
            'fontSize', 'fontFamily',
            { label: 'Font color', icon: 'plus', items: ['fontColor', 'fontBackgroundColor'] },
            '|', '-', 
            'bold', 'italic', 'underline',
            { label: 'Formatting', icon: 'text', items: ['strikethrough', 'subscript', 'superscript', 'code', 'horizontalLine', '|', 'removeFormat'] },
            'specialCharacters', 'pageBreak', '|',
            'link', 'insertImage', 'insertTable',
            { label: 'Insert', icon: 'plus', items: ['highlight', 'blockQuote', 'mediaEmbed', 'codeBlock', 'htmlEmbed'] },
            '|', 'alignment', '|',
            'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent', '|',
            'sourceEditing'
          ],
          shouldNotGroupWhenFull: true
        },
        language: 'en',
        image: {
          styles: ['alignCenter', 'alignLeft', 'alignRight'],
          resizeOptions: [
            { name: 'resizeImage:original', label: 'Original', value: null },
            { name: 'resizeImage:50', label: '50%', value: '50' },
            { name: 'resizeImage:75', label: '75%', value: '75' }
          ],
          toolbar: ['imageTextAlternative', 'toggleImageCaption', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage']
        },
        imageInsert: {
          integrations: ['insertImageViaUrl']
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'toggleTableCaption']
        },
        htmlSupport: {
          allow: [{ name: /^.*$/, styles: true, attributes: true, classes: true }]
        },
        fontFamily: { supportAllValues: true },
        fontSize: { options: [10, 12, 14, 'default', 18, 20, 22], supportAllValues: true },
        htmlEmbed: { showPreviews: true },
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        list: { properties: { styles: true, startIndex: true, reversed: true } },
        link: {
          decorators: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            toggleDownloadable: { mode: 'manual', label: 'Downloadable', attributes: { download: 'file' } }
          }
        }
      },

      campaign: {
        name: null,
        publishedAt: null,
        senderName: null,
        sender: null,
        responder: null,
        html: "",
        distributionLists: [],
        campaignTypes: [],
        attachments: [],
        emails: []
      },
      emailsText: '',

      limitForm: false,
      topmenu: [
        { name: 'Volver', path: '/communications/campaign/list', brand: 'fa fa-arrow-left' }
      ],
      distributionLists: [],
      campaignTypes: [],
      timePickerOptions: { start: '00:00', step: '00:05', end: '23:55' },
      lang: {
        days: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        pickers: ['next 7 days', 'next 30 days', 'previous 7 days', 'previous 30 days'],
        placeholder: { date: 'Seleccione fecha y hora' }
      },
      emailValidation: null,
      responseEmailValidation: null,
      emailsValidation: null,
      isDuplicate: false,
    };
  },

  async mounted() {
    this.$eventHub.$emit('captureMenu', this.topmenu);
    this.uuid = this.$route.params.uuid;
    this.typeOfTemplate = this.$route.query.typeOfTemplate;

    await this.getDistributions();
    await this.getCampaignTypes();

    this.checkDuplicateData();

    if (this.selectedEditor === 'grapes') {
      await this.$nextTick();
      this.initGrapes();
    }
  },

  methods: {
    switchEditor(editor) {
  if (this.selectedEditor === editor) return;

  let currentHtml = '';

  if (this.selectedEditor === 'grapes') {
    currentHtml = this.getGrapesHtml();
    if (this.gjs) {
      this.gjs.destroy();
      this.gjs = null;
    }
    this._gjsOriginalHtml = null;
    this._gjsUserEdited = false;
    this._gjsReady = false;
  } else if (this.selectedEditor === 'ckeditor') {
    const cleanSnippetHtml = this.unwrapAllHtmlSnippets(this.campaign.html);
    currentHtml = this.unscopeHtmlStyles(cleanSnippetHtml);
  }

  this.selectedEditor = editor;

  if (editor === 'ckeditor') {
    const scopedHtml = this.scopeHtmlStyles(currentHtml);
    this.campaign.html = `<div class="raw-html-embed">${scopedHtml}</div>`;
  } else if (editor === 'grapes') {
    this.campaign.html = currentHtml;
    this.$nextTick(() => {
      this.initGrapes(currentHtml);
    });
  }
},

    // ─── GrapesJS ─────────────────────────────────────────────────────────────

    initGrapes(htmlOverride) {
      if (this.gjs) {
        this.gjs.destroy();
        this.gjs = null;
      }

      const htmlToLoad = normalizeCampaignHtml(htmlOverride || this.campaign.html);
      this._gjsOriginalHtml = htmlToLoad;
      this._gjsUserEdited = false;
      this._gjsReady = false;

      this.gjs = grapesjs.init({
        container: "#gjs",
        fromElement: false,
        height: "800px",
        width: "auto",
        storageManager: false,
        protectedCss: '',

        assetManager: {
          upload: false,
          showUrlInput: true,
        },

        plugins: [newsletter],
        pluginsOpts: { [newsletter]: {} },

        domComponents: { 
          storeWrapper: false 
        },

        components: htmlToLoad || '',  

        canvas: {
          styles: [
            "https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css"
          ]
        }
      });

      this.gjs.on('load', () => {
        this._gjsReady = true;
      });

      this.gjs.on('update', () => {
        if (!this._gjsReady) return;
        this._gjsUserEdited = true;
      });
    },

    unwrapAllHtmlSnippets(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      
      const snippets = div.querySelectorAll('.raw-html-embed');
      
      snippets.forEach(snippet => {
        const parent = snippet.parentNode;
        if (parent) {
          while (snippet.firstChild) {
            parent.insertBefore(snippet.firstChild, snippet);
          }
          parent.removeChild(snippet);
        }
      });
      
      return div.innerHTML;
    },

    scopeHtmlStyles(html) {
  if (!html) return '';
  return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
    const scopedCss = css.replace(/([^\r\n,{}]+)(?=\s*\{)/g, (selectorMatch) => {
      let cleanSelector = selectorMatch.trim();
      if (cleanSelector.startsWith('@') || cleanSelector.startsWith('from') || cleanSelector.startsWith('to') || /^\d+%/.test(cleanSelector)) {
        return selectorMatch;
      }
      return cleanSelector.split(',').map(sel => {
        let s = sel.trim();
        if (s === 'html' || s === 'body') return '.raw-html-embed';
        if (s.startsWith('html') || s.startsWith('body')) return '.raw-html-embed' + s.replace(/^(html|body)/, '');
        return '.raw-html-embed ' + s;
      }).join(', ');
    });
    return `<style>${scopedCss}</style>`;
  });
},

unscopeHtmlStyles(html) {
  if (!html) return '';
  return html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
    const unscopedCss = css.replace(/([^\r\n,{}]+)(?=\s*\{)/g, (selectorMatch) => {
      return selectorMatch.split(',').map(sel => {
        let s = sel.trim();
        if (s === '.raw-html-embed') return 'html, body';
        if (s.startsWith('.raw-html-embed ')) {
          return s.replace(/^\.raw-html-embed\s+/, '');
        }
        return s;
      }).join(', ');
    });
    return `<style>${unscopedCss}</style>`;
  });
},

    // ─── Duplicate ────────────────────────────────────────────────────────────

    checkDuplicateData() {
      if (!this.$route.query.duplicate) return;
      try {
        const json = decodeURIComponent(escape(atob(this.$route.query.duplicate)));
        const d = JSON.parse(json);

        this.isDuplicate = true;
        this.campaign.name        = d.name        || null;
        this.campaign.senderName  = d.senderName  || null;
        this.campaign.sender      = d.sender      || null;
        this.campaign.responder   = d.responder   || null;
        this.campaign.publishedAt = new Date();

        let rawHtml = d.html || '';
        
        if (this.selectedEditor === 'ckeditor' && rawHtml) {
          const scopedHtml = this.scopeHtmlStyles(rawHtml);
          this.campaign.html = `<div class="raw-html-embed">${scopedHtml}</div>`;
        } else {
          this.campaign.html = rawHtml;
        }

        this.campaign.distributionLists = (d.distributionLists || [])
          .map(item => this.distributionLists.find(dl => dl.uuid === item.uuid))
          .filter(Boolean);

        this.campaign.campaignTypes = (d.campaignTypes || [])
          .map(item => this.campaignTypes.find(ct => ct.uuid === item.uuid))
          .filter(Boolean);

        if (d.emails && d.emails.length > 0) {
          this.campaign.emails = d.emails;
          this.emailsText = d.emails.join(', ');
        }

      } catch (error) {
        console.error('Error al decodificar datos de duplicación:', error);
      }
    },

    getGrapesHtml() {
      if (!this._gjsUserEdited) {
        return this._gjsOriginalHtml || '';
      }
      const inlineHtml = this.gjs.runCommand('gjs-get-inlined-html');
      return inlineHtml || '';
    },

    // ─── Validations ──────────────────────────────────────────────────────────

    getEmailValidationState() { return this.emailValidation; },
    getEmailErrorMessage() {
      if (this.campaign.sender && this.emailValidation === false)
      return null;
    },
    validateEmail() {
      this.emailValidation = /^[a-zA-Z0-9._%+-]+@ata\.es$/.test(this.campaign.sender);
    },
    getResponseEmailValidationState() { return this.responseEmailValidation; },
    getResponseEmailErrorMessage() {
      if (this.campaign.responder && this.responseEmailValidation === false)
      return null;
    },
    validateResponseEmail() {
      this.responseEmailValidation = /^[a-zA-Z0-9._%+-]+@ata\.es$/.test(this.campaign.responder);
    },
    
    updateEmails() {
      const emails = this.emailsText
        .split(/[, \r\n]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);
      
      this.campaign.emails = emails;
      this.validateEmails();
    },
    
    validateEmails() {
      if (!this.campaign.emails || this.campaign.emails.length === 0) {
        this.emailsValidation = null; 
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const allValid = this.campaign.emails.every(email => emailRegex.test(email));
      this.emailsValidation = allValid;
    },
    
    getEmailsValidationState() { return this.emailsValidation; },
    getEmailsErrorMessage() {
      if (this.emailsValidation === false) {
        return 'Uno o más emails no son válidos';
      }
      return null;
    },

    // ─── API ──────────────────────────────────────────────────────────────────

    async getDistributions() {
      this.$_loadingMixin_showLoading();
      await getDistributions().then(res => { this.distributionLists = res.distributionLists; });
      this.$_loadingMixin_hideLoading();
    },
    async getCampaignTypes() {
      this.$_loadingMixin_showLoading();
      await getCampaignTypes().then(res => { this.campaignTypes = res.campaignTypes; });
      this.$_loadingMixin_hideLoading();
    },

    async store() {
      this.submitAttempted = true;
      if (!this.requirements()) return;

      let finalHtml = '';
      if (this.selectedEditor === 'grapes' && this.gjs) {
        finalHtml = this.getGrapesHtml();
      } else {
        const cleanSnippetHtml = this.unwrapAllHtmlSnippets(this.campaign.html);
        finalHtml = this.unwrapAllHtmlSnippets(cleanSnippetHtml);
      }

      this.$_loadingMixin_showLoading();
      const formData = new FormData();

      formData.append('name',        this.campaign.name);
      formData.append('html',        finalHtml);
      formData.append('publishedAt', this.campaign.publishedAt || null);
      formData.append('senderName',  this.campaign.senderName  || null);
      formData.append('sender',      this.campaign.sender      || null);
      formData.append('responder',   this.campaign.responder   || null);

      if (this.campaign.distributionLists && this.campaign.distributionLists.length > 0) {
        this.campaign.distributionLists.forEach(d => formData.append('distributions[]', d.uuid));
      } else {
        formData.append('distributions', null);
      }

      if (this.campaign.campaignTypes && this.campaign.campaignTypes.length > 0) {
        this.campaign.campaignTypes.forEach(c => formData.append('campaignTypes[]', c.uuid));
      } else {
        formData.append('campaignTypes', null);
      }

      if (this.campaign.attachments && this.campaign.attachments.length > 0) {
        for (let i = 0; i < this.campaign.attachments.length; i++) {
          formData.append('attachments[]', this.campaign.attachments[i]);
        }
      } else {
        formData.append('attachments', null);
      }
      
      if (this.campaign.emails && this.campaign.emails.length > 0) {
        this.campaign.emails.forEach(email => formData.append('emails[]', email));
      } else {
        formData.append('emails', null);
      }

      try {
        const response = await axios.post('campaign', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.status === 201 || response.status === 204) {
          this.$_notificationMixin_success('Operación Completada', 'Realizado con éxito');
          this.$router.push({ path: '/communications/campaign/list' });
        }
      } catch (error) {
        this.$_loadingMixin_hideLoading();
      }
    },

    requirements() {
      return !!(this.campaign.html && this.campaign.name);
    },
  },

  watch: {
    'campaign.sender'()    { this.validateEmail(); },
    'campaign.responder'() { this.validateResponseEmail(); },
    'campaign.emails'()    { this.validateEmails(); },
  },

  beforeDestroy() {
    if (this.gjs) {
      this.gjs.destroy();
      this.gjs = null;
    }
  }
};
</script>

<style>
.gjs-am-file-uploader {
  display: none !important;
}

.editor-toggle {
  display: inline-flex;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.editor-toggle-btn {
  padding: 8px 18px;
  background: #f8f9fa;
  border: none;
  border-right: 1px solid #dee2e6;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
  transition: background 0.15s, color 0.15s;
}

.editor-toggle-btn:last-child {
  border-right: none;
}

.editor-toggle-btn:hover {
  background: #e9ecef;
}

.editor-toggle-btn.active {
  background: #025542;
  color: #fff;
}

.ck.ck-file-dialog-button {
  display: none !important;
}
</style>
